import { type TooltipModel, type ScriptableTooltipContext } from "chart.js";
import moment from "moment";

import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { REVENUE_ID, ROAS_ID } from "./const";

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  const options = context.tooltip.options;

  let tooltipEl = chart.canvas?.parentNode?.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-derived-kpi-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    chart.canvas.parentNode?.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body) {
    const content = buildTooltipContent(tooltip, options);

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          style="min-width: 550px;"
          class="
            bg-v2-primary border border-v2-secondary py-3 px-4 rounded-md space-y-2
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    left: positionX + tooltip.caretX + "px",
    top: positionY + tooltip.caretY + "px",
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  options: any,
) => {
  const potentialDateLabel = tooltip.dataPoints[0].label;
  return `
    <div class="font-semibold">
    ${
      moment(tooltip.dataPoints[0].label).isValid()
        ? `${moment(potentialDateLabel).format("MMM D, YYYY")} - ${moment(
            potentialDateLabel,
          )
            .add(6, "days")
            .format("MMM D, YYYY")}`
        : potentialDateLabel
    }
    </div>
    <div class="grid grid-cols-2 text-xs gap-2">

      ${tooltip.dataPoints
        .map(({ dataset, raw }) => {
          let value = raw;

          if (dataset.label === "Spend") {
            value = MetricFormatters.currency(
              Number(value),
              options.user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            );
          } else if (dataset.yAxisID === REVENUE_ID) {
            value = options.isRevenue
              ? MetricFormatters.currency(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  {
                    precision: "exact",
                  },
                )
              : MetricFormatters.decimal(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  {
                    precision: "exact",
                  },
                );
          } else if (dataset.yAxisID === ROAS_ID) {
            value = options.isRevenue
              ? MetricFormatters.multiplier(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  {
                    precision: "exact",
                  },
                )
              : MetricFormatters.currency(
                  Number(value),
                  options.user.currencies.currentCurrency,
                  {
                    precision: "exact",
                  },
                );
          }

          const legend = buildLegend(
            {
              borderColor: dataset.borderColor,
              backgroundColor: dataset.backgroundColor,
              borderDash: dataset.borderDash ?? [],
            } as any,
            dataset.type === "bar",
          );

          return `
            <div class="flex gap-0.5 items-center">
              <div class="shrink-0 width: 30px;">${legend}</div>
              <div>${`${dataset.label}`}</div>
            </div>
            <div class="text-right font-mono">${value}</div>
          `;
        })
        .join("")}
    </div>
  `;
};
